// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { selectOpenTrades } from '@advisor-app/store/slices/trader';
import { selectSummary } from '@advisor-app/store/slices/broker';
import { useAppSelector } from '@advisor-app/store/store';
import { useMemo } from 'react';
import type { KpiBoxesProps } from '@shared/ui-kit/components';
import { selectAccountCurrency } from '@shared/data-access/slices/user';

export const useOpenTrades = () => {
  const { cash } = useAppSelector(selectSummary);

  const trades = useAppSelector(selectOpenTrades);

  const currency = useAppSelector(selectAccountCurrency);

  const buyTrades = useMemo(
    () => trades.filter(item => item.side === 'buy'),
    [trades],
  );

  const buyTotal = useMemo(
    () => buyTrades.reduce((total, { volume, price }) => total + volume * price, 0),
    [buyTrades],
  );

  const sellTrades = useMemo(
    () => trades.filter(item => item.side === 'sell'),
    [trades],
  );

  const sellTotal = useMemo(
    () =>
      sellTrades.reduce((total, { volume, price }) => total + volume * price, 0),
    [sellTrades],
  );

  const kpiData: KpiBoxesProps[] = useMemo(
    () => [
      {
        value: formatToMoney(Math.trunc(buyTotal - sellTotal), undefined, 0),
        text: 'Net new Investment',
        cash: cash,
        currency,
        icon: 'TrendingUp',
        color: 'main',
      },
      {
        value: formatToMoney(Math.trunc(buyTotal)),
        text: 'Buy',
        numberPositions: buyTrades.length,
        icon: 'CircleArrowUp',
        color: 'success',
      },
      {
        value: formatToMoney(Math.trunc(sellTotal)),
        text: 'Sell',
        numberPositions: sellTrades.length,
        icon: 'CircleArrowDown',
        color: 'danger',
      },
      {
        value: formatToMoney(Math.trunc(0)),
        text: 'Realized Gains',
        icon: 'DollarSign',
        color: 'success',
      },
    ],
    [buyTotal, buyTrades.length, cash, currency, sellTotal, sellTrades.length],
  );

  return { trades, kpiData };
};
